import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticlesByCategory, getCategoryBySlug, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const categoryArticles = getArticlesByCategory(slug || '');
  const [visibleCount, setVisibleCount] = useState(8);

  if (!category) {
    return (
      <>
        <SEOHead title="分类不存在" />
        <Navbar />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 text-center">
          <div className="w-20 h-20 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类不存在</h1>
          <p className="text-sm text-foreground-500 mb-6">您访问的分类页面不存在，可能已被移除。</p>
          <Link to="/" className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const displayedArticles = categoryArticles.slice(0, visibleCount);
  const hasMore = visibleCount < categoryArticles.length;

  const categorySchema = {
    '@type': 'CollectionPage',
    name: `${category.name} - 吃瓜网站`,
    description: category.description,
    url: `https://www.chigua.site/category/${category.slug}`,
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 吃瓜网站`}
        description={category.description}
        keywords={`${category.name},吃瓜网站,51吃瓜网站,吃瓜网站线路一,网红吃瓜,吃瓜爆料`}
        ogType="website"
        schema={categorySchema}
      />

      <Navbar />

      <main>
        <section className="bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />

            <div className="mt-6">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className={`${category.icon} text-lg text-primary-600`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}
                </h1>
              </div>
              <p className="text-sm text-foreground-500 max-w-2xl leading-relaxed">{category.description}</p>
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {displayedArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>

                {categoryArticles.length === 0 && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-sm text-foreground-500">该分类暂无内容</p>
                  </div>
                )}

                {hasMore && (
                  <div className="flex justify-center mt-8">
                    <button
                      onClick={() => setVisibleCount((prev) => Math.min(prev + 8, categoryArticles.length))}
                      className="px-8 py-3 rounded-full border-2 border-secondary-300 text-sm font-medium text-foreground-700 hover:border-primary-500 hover:text-primary-600 transition-all duration-200 bg-background-50 cursor-pointer whitespace-nowrap"
                    >
                      加载更多
                      <i className="ri-arrow-down-s-line ml-1 w-4 h-4 inline-flex items-center justify-center"></i>
                    </button>
                  </div>
                )}
              </div>

              <aside className="w-full lg:w-72 flex-shrink-0">
                <div className="bg-background-100 rounded-lg p-5 border border-secondary-200/70 sticky top-24">
                  <h3 className="text-sm font-heading font-bold text-foreground-900 mb-4">全部分类</h3>
                  <div className="space-y-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                          cat.slug === slug
                            ? 'bg-primary-500 text-background-50 font-medium'
                            : 'text-foreground-700 hover:bg-secondary-200/70'
                        }`}
                      >
                        <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                        {cat.name}
                        <span className="ml-auto text-xs opacity-70">
                          {getArticlesByCategory(cat.slug).length}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}